import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getToken, setToken, tzOffset } from "@/lib/api";
import { track } from "@/lib/analytics";
import { setCurrentResume } from "@/lib/atsScores";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export const profileComplete = (p) =>
  !!p && (!!p.resume_file || (p.resume_text || "").length >= 200) && !!p.headline && (p.skills || []).length >= 1;

const ADMINS = (process.env.REACT_APP_ADMIN_EMAILS || "").split(",").map((s) => s.trim().toLowerCase());
// Limited admin access: "email:tier:area+area" entries, comma separated (server must enforce the same).
const PARTNERS = Object.fromEntries((process.env.REACT_APP_PARTNER_ACCESS || "").split(",").filter(Boolean).map((e) => { const [email, tier, areas] = e.trim().split(":"); return [email.toLowerCase(), { tier, areas: (areas || "").split("+").filter(Boolean) }]; }));

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading");
  const [profile, setProfile] = useState(null);
  const [billing, setBilling] = useState(null);

  const refreshProfile = useCallback(async () => {
    const { data } = await api.get("/profile");
    setProfile(data);
    setCurrentResume(data?.resume_file?.name);
    return data;
  }, []);

  const refreshBilling = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/status");
      setBilling(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const bootstrap = useCallback(
    async (u) => {
      // §82 Client portal sign-in: if the session user lacks the client grant, ask the workspace endpoint.
      if (!u?.access_grant && localStorage.getItem("rpopro_portal") === "client") {
        const g = await api.get("/client/me").then((r) => r.data?.access_grant).catch(() => null);
        if (g) u = { ...u, access_grant: g };
      }
      setUser(u);
      api.post("/me/timezone", { tz_offset_minutes: tzOffset() }).catch(() => {});
      const ref = localStorage.getItem("rpopro_ref");
      if (ref) {
        localStorage.removeItem("rpopro_ref");
        api.post("/referrals/claim", { code: ref }).catch(() => {});
      }
      await Promise.all([refreshProfile().catch(() => setProfile({})), refreshBilling()]);
      setStatus("authed");
    },
    [refreshProfile, refreshBilling]
  );

  const logoutLocal = useCallback(() => {
    setToken(null);
    setUser(null);
    setProfile(null);
    setBilling(null);
    setStatus("anon");
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    if (!getToken()) {
      setStatus("anon");
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => bootstrap(data.user || data))
      .catch(logoutLocal);
  }, [bootstrap, logoutLocal]);

  useEffect(() => {
    window.addEventListener("rpopro:logout", logoutLocal);
    return () => window.removeEventListener("rpopro:logout", logoutLocal);
  }, [logoutLocal]);

  const loginWithSession = async (session_id) => {
    try {
      const { data } = await api.post("/auth/session", { session_id });
      setToken(data.session_token);
      const c = data.user?.created_at && Date.now() - new Date(data.user.created_at).getTime() < 15 * 60000;
      if (data.is_new || data.new_user || c) track("sign_up");
      await bootstrap(data.user);
    } catch (e) {
      logoutLocal();
      throw e;
    }
  };

  const loginWithPassword = async (path, body) => {
    const { data } = await api.post(`/auth/${path}`, body);
    setToken(data.session_token || data.token || data.access_token);
    if (path === "register" || data.is_new) track("sign_up");
    await bootstrap(data.user || (await api.get("/auth/me")).data?.user);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    logoutLocal();
  };

  const isAdmin = !!user && (ADMINS.includes((user.email || "").toLowerCase()) || billing?.admin === true || billing?.is_admin === true);
  const grant = !user || isAdmin ? null : user.access_grant || PARTNERS[(user.email || "").toLowerCase()] || null;
  const cm = grant?.kind === "client" ? { radar: [], ...(grant.modules || {}) } : null;
  const adminAreas = isAdmin ? ["all"] : cm ? [...(cm.radar?.length || cm.workflows !== false ? ["rcc"] : []), ...(cm.si ? ["si"] : [])] : grant?.areas || [];
  const canAdmin = (area) => isAdmin || (!!area && adminAreas.includes(area));
  const tier = isAdmin ? "elite" : (grant?.tier || billing?.tier || user?.tier || "free").toLowerCase();
  const client = user?.access_grant?.kind === "client" && (!isAdmin || localStorage.getItem("rpopro_portal") === "client") ? user.access_grant : null;

  return (
    <AuthCtx.Provider
      value={{ user, status, profile, billing, tier, isAdmin, adminAreas, canAdmin, client, loginWithSession, loginWithPassword, logout, refreshProfile, refreshBilling, setProfile }}
    >
      {children}
    </AuthCtx.Provider>
  );
};
